import { t } from '@lingui/core/macro';
import { useEffect, useRef } from 'react';

import { type ChatMessageType } from '@/api/ai';
import { AISearchInput } from '@/components/Form';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/Sheet';

import AIMessage from './AIMessage';
import { getLocalizedStatusLabel, type RequestStatus } from './statusState';

type Props = {
  open: boolean;
  messages: ChatMessageType[];
  isAIResponseLoading?: boolean;
  responseStatus?: RequestStatus;
  isAIStreamingResponse?: boolean;
  onClose: () => void;
  onSendMessage: (message: string) => void;
};

const AIDrawer = ({
  open,
  messages,
  isAIResponseLoading = false,
  responseStatus,
  isAIStreamingResponse = false,
  onClose,
  onSendMessage,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollDown = ({ isSmooth = false } = {}) => {
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: isSmooth ? 'smooth' : 'auto',
        });
      }
    });
  };

  const handleSearchMessage = (value: string) => {
    onSendMessage(value);
    scrollDown({ isSmooth: true });
  };

  useEffect(() => {
    if (open) {
      scrollDown({ isSmooth: true });
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (containerRef.current && messages.length > 0) {
      const buffer = 80;
      const isAtBottom =
        containerRef.current.clientHeight + containerRef.current.scrollTop >=
        containerRef.current.scrollHeight - buffer;
      if (isAtBottom) {
        scrollDown({ isSmooth: false });
      }
    }
  }, [messages]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <SheetContent
        side="right"
        className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-5xl"
      >
        <SheetHeader>
          <SheetTitle>{t`C99 Assistant`}</SheetTitle>
        </SheetHeader>
        <div ref={containerRef} className="flex flex-col gap-4 overflow-y-auto p-6">
          {messages.map((message, index) => (
            <AIMessage type={message.role} key={index}>
              {message.content}
            </AIMessage>
          ))}
          {isAIResponseLoading && (
            <AIMessage type="loading">{getLocalizedStatusLabel(responseStatus)}</AIMessage>
          )}
        </div>
        <SheetFooter>
          <AISearchInput
            ref={inputRef}
            size="large"
            showSendButton
            isSearchDisabled={isAIStreamingResponse}
            onSearch={handleSearchMessage}
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AIDrawer;
